/**
 * Locale-aware date/time formatting for UI timestamps.
 * Arabic uses ar-SA (Eastern Arabic numerals + Arabic day-period markers).
 */

export function resolveDateLocale(language) {
  if (!language) return "en-US";
  if (language.startsWith("ar")) return "ar-SA";
  if (language.startsWith("en")) return "en-US";
  return language;
}

function toValidDate(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

const DATE_OPTS = {
  day: "numeric",
  month: "numeric",
  year: "numeric",
};

const TIME_OPTS_HM = {
  hour: "2-digit",
  minute: "2-digit",
  hour12: true,
};

const TIME_OPTS_HMS = {
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hour12: true,
};

/**
 * @param {string|Date} value
 * @param {string} [localeHint] - e.g. "ar-SA" or "en-US"
 * @param {"-"|"—"|string} [fallback]
 */
export function formatLocalizedDate(value, localeHint, fallback = "-") {
  const d = toValidDate(value);
  if (!d) return fallback;
  try {
    return d.toLocaleDateString(localeHint || "en-US", DATE_OPTS);
  } catch {
    return d.toLocaleDateString("en-US", DATE_OPTS);
  }
}

/**
 * @param {string|Date} value
 * @param {string} [localeHint]
 * @param {{ withSeconds?: boolean, fallback?: string }} [options]
 */
export function formatLocalizedTime(value, localeHint, options = {}) {
  const { withSeconds = false, fallback = "-" } = options;
  const d = toValidDate(value);
  if (!d) return fallback;
  const opts = withSeconds ? TIME_OPTS_HMS : TIME_OPTS_HM;
  try {
    return d.toLocaleTimeString(localeHint || "en-US", opts);
  } catch {
    return d.toLocaleTimeString("en-US", opts);
  }
}

/**
 * Single-line date + time (e.g. products / category tables).
 * @param {string|Date} value
 * @param {string} [localeHint]
 * @param {{ withSeconds?: boolean, fallback?: string }} [options]
 */
export function formatLocalizedDateTime(value, localeHint, options = {}) {
  const { withSeconds = false, fallback = "-" } = options;
  const d = toValidDate(value);
  if (!d) return fallback;
  try {
    return d.toLocaleString(localeHint || "en-US", {
      ...DATE_OPTS,
      ...(withSeconds ? TIME_OPTS_HMS : TIME_OPTS_HM),
    });
  } catch {
    return `${formatLocalizedDate(value, "en-US", fallback)} ${formatLocalizedTime(value, "en-US", { withSeconds, fallback: "" })}`.trim() || fallback;
  }
}

/**
 * Split date/time for stacked table cells (reports, stores, addons).
 * @param {string|Date} value
 * @param {string} [localeHint]
 * @param {{ withSeconds?: boolean, fallback?: string }} [options]
 * @returns {{ date: string, time: string }}
 */
export function formatLocalizedDateTimeParts(value, localeHint, options = {}) {
  const { withSeconds = true, fallback = "-" } = options;
  const d = toValidDate(value);
  if (!d) return { date: fallback, time: fallback };
  return {
    date: formatLocalizedDate(value, localeHint, fallback),
    time: formatLocalizedTime(value, localeHint, { withSeconds, fallback }),
  };
}
